// WHAT: Tab component for Withings overview dashboard with trends and metrics
// WHY:  Provides comprehensive view of health measurements with trend analysis
// DOMAIN: Health → Withings
// DEPENDENCIES: LineChart, WithingsLoader, WithingsMeasurement types
// RISKS:
//   - NOTE: Trend calculation may not handle edge cases (empty data, single point)
//   - NOTE: Date range filtering may have timezone issues
//   - NOTE: Error handling may not provide user-friendly messages

import type { WithingsMeasurement } from "../../types";
import type { WithingsMeasurement as DatabaseWithingsMeasurement } from "../../types/database";

import { AlertCircle, TrendingUp } from "lucide-react";
import React from "react";

import { LineChart, type LineChartDataPoint } from "@/components/charts/primitives/LineChart";
import { Card } from "@/components/ui/Card";

import { MetricCard } from "../molecules/MetricCard";
import { WithingsLoader } from "../ui/WithingsLoader";

type DateRange = "1y" | "7d" | "30d" | "90d";

type TrendDirection = "negative" | "neutral" | "positive";

interface TrendData {
  change: number;
  firstDate: string;
  lastDate: string;
  percentage: number;
  trend: TrendDirection;
}

/**
 * WHAT:
 *   Calculates trend data based on date range.
 *
 * WHY:
 *   Provides trend indicators for health metrics.
 *
 * BEHAVIOR:
 *   - Filters data by date range
 *   - Compares first and last values
 *   - Calculates percentage change
 *   - Determines trend direction
 *
 * @param {WithingsMeasurement[]} data - Measurement data array
 *
 * @param {keyof WithingsMeasurement} key - Key to extract value from measurements
 *
 * @param {DateRange} dateRange - Date range for filtering
 *
 * @returns {TrendData} Trend data with change, percentage, and direction
 *
 */
const calculateTrend = (
  data: WithingsMeasurement[],
  key: keyof WithingsMeasurement,
  dateRange: DateRange,
): TrendData => {
  if (data.length < 2) {
    return {
      change: 0,
      firstDate: "",
      lastDate: "",
      percentage: 0,
      trend: "neutral",
    };
  }

  // NOTE: Filter data based on date range
  const now = new Date();
  const filteredData: WithingsMeasurement[] = data.filter((measurement: WithingsMeasurement) => {
    const measurementDate = new Date(measurement.measurement_date);
    const daysDiff = (now.getTime() - measurementDate.getTime()) / (1000 * 60 * 60 * 24);

    switch (dateRange) {
      case "7d": {
        return daysDiff <= 7;
      }
      case "30d": {
        return daysDiff <= 30;
      }
      case "90d": {
        return daysDiff <= 90;
      }
      case "1y": {
        return daysDiff <= 365;
      }
      default: {
        return true;
      }
    }
  });

  if (filteredData.length < 2) {
    return {
      change: 0,
      firstDate: "",
      lastDate: "",
      percentage: 0,
      trend: "neutral",
    };
  }

  // Sortiere nach Datum (älteste zuerst)
  // Reason: toSorted() returns a new array, but TypeScript may infer unsafe type

  const sortedData: WithingsMeasurement[] = filteredData.toSorted(
    (a: WithingsMeasurement, b: WithingsMeasurement) =>
      new Date(a.measurement_date).getTime() - new Date(b.measurement_date).getTime(),
  );

  const first: undefined | WithingsMeasurement = sortedData[0];
  const lastIndex = sortedData.length - 1;
  const last: undefined | WithingsMeasurement = lastIndex >= 0 ? sortedData[lastIndex] : undefined;

  if (!last) {
    return {
      change: 0,
      firstDate: "",
      lastDate: "",
      percentage: 0,
      trend: "neutral",
    };
  }

  // Extrahiere den Wert basierend auf dem Key
  if (!first) {
    return {
      change: 0,
      firstDate: "",
      lastDate: "",
      percentage: 0,
      trend: "neutral",
    };
  }
  const firstValue: unknown = first[key];
  const lastValue: unknown = last[key];

  if (typeof firstValue !== "number" || typeof lastValue !== "number") {
    return {
      change: 0,
      firstDate: "",
      lastDate: "",
      percentage: 0,
      trend: "neutral",
    };
  }

  const change = lastValue - firstValue;
  const percentage = firstValue === 0 ? 0 : (change / firstValue) * 100;

  let trend: TrendDirection = "neutral";
  if (change > 0.1) {
    trend = "positive";
  } else if (change < -0.1) {
    trend = "negative";
  }

  return {
    change,
    firstDate: first.measurement_date,
    lastDate: last.measurement_date,
    percentage,
    trend,
  };
};

interface OverviewTabProperties {
  dateRange: DateRange;
  error?: null | string;
  isLoading?: boolean;
  measurements: WithingsMeasurement[];
  onDateRangeChange: (range: DateRange) => void;
}

/**
 * WHAT:
 *   Tab component that displays Withings health measurement overview with
 *   trend indicators, charts, and key metrics.
 *
 * WHY:
 *   Enables users to see their health data trends and current status at a glance.
 *
 * PROPS:
 *   - measurements: WithingsMeasurement[] — Measurement data
 *   - dateRange: DateRange — Selected date range
 *   - onDateRangeChange: (range) => void — Date range change handler
 *   - isLoading?: boolean — Loading state
 *   - error?: string | null — Error message
 *
 * BEHAVIOR:
 *   - Displays error state if error occurs
 *   - Shows loading state while fetching
 *   - Displays empty state if no data
 *   - Calculates trends based on date range
 *   - Renders charts and metrics
 *
 * RISKS:
 *   - NOTE: Trend calculation may not handle edge cases (empty data, single point)
 *   - NOTE: Date range filtering may have timezone issues
 *   - NOTE: Error handling may not provide user-friendly messages
 *
 * @param root0
 *
 * @param root0.measurements
 *
 * @param root0.dateRange
 *
 * @param root0.onDateRangeChange
 *
 * @param root0.isLoading
 *
 * @param root0.error
 *
 * @returns {JSX.Element} Overview tab component with health metrics and trends
 *
 */
export const OverviewTab: React.FC<OverviewTabProperties> = ({
  dateRange,
  error = null,
  isLoading = false,
  measurements,
  onDateRangeChange,
}) => {
  // NOTE: Show error state
  if (error) {
    return (
      <div className="space-y-6">
        <Card className="bg-slate-800/50 border-slate-700 text-white">
          <div className="p-6 text-center">
            <AlertCircle className="h-12 w-12 text-red-500 mx-auto mb-4" />
            <h3 className="text-lg font-semibold text-white mb-2">Error Loading Data</h3>
            <p className="text-slate-400 mb-4">{error}</p>
            <button
              className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 transition-colors"
              onClick={() => {
                globalThis.location.reload();
              }}
            >
              Try Again
            </button>
          </div>
        </Card>
      </div>
    );
  }

  // NOTE: Show loading state if no measurements and still loading
  if (isLoading && measurements.length === 0) {
    return <WithingsLoader />;
  }

  // NOTE: Show empty state if no measurements
  if (!isLoading && measurements.length === 0) {
    return (
      <div className="space-y-6">
        <Card className="bg-slate-800/50 border-slate-700 text-white">
          <div className="p-6 text-center">
            <TrendingUp className="h-12 w-12 text-slate-400 mx-auto mb-4" />
            <h3 className="text-lg font-semibold text-white mb-2">No Measurement Data Available</h3>
            <p className="text-slate-400 mb-4">
              No Withings measurements found for the selected time period.
            </p>
            <div className="flex justify-center space-x-2">
              {(["7d", "30d", "90d", "1y"] as DateRange[]).map((range) => (
                <button
                  className={`px-3 py-1 rounded-md text-sm font-medium transition-all duration-200 ${
                    dateRange === range
                      ? "bg-blue-600 text-white"
                      : "text-slate-400 hover:text-white hover:bg-slate-600"
                  }`}
                  key={range}
                  onClick={() => {
                    onDateRangeChange(range);
                  }}
                >
                  {range === "7d" && "7 Days"}
                  {range === "30d" && "30 Days"}
                  {range === "90d" && "90 Days"}
                  {range === "1y" && "1 Year"}
                </button>
              ))}
            </div>
          </div>
        </Card>
      </div>
    );
  }

  const latest: undefined | WithingsMeasurement = measurements[0];

  // Dynamische Trends für alle Metriken
  const weightTrend = calculateTrend(measurements, "weight_kg", dateRange);
  const bodyFatTrend = calculateTrend(measurements, "body_fat_percent", dateRange);
  const muscleMassTrend = calculateTrend(measurements, "muscle_mass_kg", dateRange);
  const fatMassTrend = calculateTrend(measurements, "fat_mass_kg", dateRange);
  const fatFreeMassPercentTrend = calculateTrend(measurements, "fat_free_mass_percent", dateRange);
  const bmiTrend = calculateTrend(measurements, "bmi", dateRange);

  return (
    <div className="space-y-6">
      {/* Zeitraum-Filter */}
      <div className="flex justify-center">
        <div className="flex space-x-1 bg-slate-800/50 rounded-lg p-1">
          {(["7d", "30d", "90d", "1y"] as const).map((range) => (
            <button
              className={`px-4 py-2 rounded-md text-sm font-medium transition-all duration-200 ${
                dateRange === range
                  ? "bg-blue-600 text-white shadow-lg"
                  : "text-slate-300 hover:text-white hover:bg-slate-600"
              }`}
              key={range}
              onClick={() => {
                onDateRangeChange(range);
              }}
            >
              {range === "7d" && "7 Days"}
              {range === "30d" && "30 Days"}
              {range === "90d" && "90 Days"}
              {range === "1y" && "1 Year"}
            </button>
          ))}
        </div>
      </div>

      {/* Stats Cards */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-6 gap-6">
        <MetricCard
          dateRange={dateRange}
          latest={latest as DatabaseWithingsMeasurement | undefined}
          title="Weight"
          trend={weightTrend}
          unit="kg"
          valueFormatter={(value) => value.toFixed(1)}
          valueKey="weight_kg"
        />

        <MetricCard
          dateRange={dateRange}
          latest={latest as DatabaseWithingsMeasurement | undefined}
          title="Body Fat"
          trend={bodyFatTrend}
          unit="%"
          valueFormatter={(value) => value.toFixed(1)}
          valueKey="body_fat_percent"
        />

        <MetricCard
          dateRange={dateRange}
          isGoodPositive
          latest={latest as DatabaseWithingsMeasurement | undefined}
          title="Muscle Mass"
          trend={muscleMassTrend}
          unit="kg"
          valueFormatter={(value) => value.toFixed(1)}
          valueKey="muscle_mass_kg"
        />

        <MetricCard
          dateRange={dateRange}
          latest={latest as DatabaseWithingsMeasurement | undefined}
          title="Fat Mass"
          trend={fatMassTrend}
          unit="kg"
          valueFormatter={(value) => value.toFixed(1)}
          valueKey="fat_mass_kg"
        />

        <MetricCard
          dateRange={dateRange}
          isGoodPositive
          latest={latest as DatabaseWithingsMeasurement | undefined}
          title="Lean Mass"
          trend={fatFreeMassPercentTrend}
          unit="%"
          valueFormatter={(value) => value.toFixed(1)}
          valueKey="fat_free_mass_percent"
        />

        <MetricCard
          dateRange={dateRange}
          latest={latest as DatabaseWithingsMeasurement | undefined}
          title="BMI"
          trend={bmiTrend}
          unit=""
          valueFormatter={(value) => value.toFixed(1)}
          valueKey="bmi"
        />

        <Card className="bg-slate-800/50 border-slate-700 text-white hover:bg-slate-800/70 transition-all duration-200">
          <div className="p-6">
            <div className="flex items-center justify-between mb-3">
              <div className="flex items-center space-x-2">
                <img
                  alt="Withings"
                  className="h-5 w-5 object-contain opacity-80"
                  src="/src/assets/withings-logo.png"
                />
                <span className="text-slate-400 text-sm font-medium">Basal Metabolic Rate</span>
              </div>
            </div>
            <p className="text-3xl font-bold text-white mb-2">
              {latest?.basal_metabolic_rate !== null && latest?.basal_metabolic_rate !== undefined
                ? `${String(latest.basal_metabolic_rate)} kcal`
                : "N/A"}
            </p>
            <div className="text-xs text-slate-500">Daily calorie requirement</div>
          </div>
        </Card>
      </div>

      {/* Weight Chart */}
      <Card className="bg-slate-800/50 border-slate-700 text-white hover:bg-slate-800/70 transition-all duration-200">
        <div className="p-6">
          <div className="flex items-center justify-between mb-6">
            <div className="flex items-center space-x-3">
              <TrendingUp className="h-6 w-6 text-blue-500" />
              <h3 className="text-xl font-semibold text-white">Weight Trend</h3>
              <div className="text-sm text-slate-400">
                {measurements.length} measurements available
              </div>
            </div>
          </div>

          <LineChart
            className="w-full"
            data={measurements.toReversed() as unknown as LineChartDataPoint[]}
            gridLines={{
              horizontal: true,
              horizontalColor: "rgba(55, 65, 81, 0.3)",
              vertical: false,
            }}
            height={400}
            xKey="measurement_date"
            {...(() => {
              const weights = measurements
                .map((m) => m.weight_kg)
                .filter((w): w is number => typeof w === "number");

              if (weights.length === 0) {
                return {};
              }

              const minWeight = Math.min(...weights);
              const maxWeight = Math.max(...weights);
              const range = maxWeight - minWeight;

              // Mindestens 5kg Range für bessere Sichtbarkeit
              const minRange = Math.max(range, 5);
              const padding = minRange * 0.1; // 10% Padding

              const domain: [number, number] = [
                Math.floor(minWeight - padding),
                Math.ceil(maxWeight + padding),
              ];
              return { yAxisDomain: domain };
            })()}
            yAxisTickFormatter={(value) => `${value.toFixed(1)} kg`}
            yKey="weight_kg"
          />
        </div>
      </Card>
    </div>
  );
};
